// src/pages/admin/manager/reservas/ProyectosPage.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  IconButton,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { Add, Edit, Delete, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context/AuthContext";
import {
  useProyectos,
  useProyectosMeta,
  useProyectoMutations,
  type ProyectosFilters,
  type ProyectoRol,
  type ProyectoBillingMode,
} from "../../../../hooks/useProyectos";
import ProyectoDialog from "../../../../components/ProyectoDialog";

/* ── helpers de saneado ─────────────────────────────────────── */
const isTruthy = (v: any): boolean => v === true || v === 1 || v === "1";

const isNonEmptyLabel = (v: any): boolean =>
  typeof v === "string" &&
  v.trim() !== "" &&
  v.trim() !== "0" &&
  v.trim() !== "000" &&
  v.trim().toLowerCase() !== "null" &&
  v.trim() !== "-";

const isHexColor = (v: any): v is string =>
  typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v.trim());

export default function ProyectosPage() {
  const { t } = useTranslation<"translation">();
  const { user } = useAuth();
  const canEdit = !!(user?.roles?.includes("admin") || user?.roles?.includes("facturacion"));

  const { data: meta } = useProyectosMeta();

  // ✅ TIPADO CORRECTO DE LOS FILTROS
  const [filters, setFilters] = useState<ProyectosFilters>({ activos: true });

  const { rows, isFetching, refetch } = useProyectos(filters);
  const { removeProyecto, activarProyecto } = useProyectoMutations();

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editRow, setEditRow] = useState<any | null>(null);

  const onCreate = () => {
    setEditRow(null);
    setDlgOpen(true);
  };
  const onEdit = (row: any) => {
    setEditRow(row);
    setDlgOpen(true);
  };

  useEffect(() => {
    void refetch();
  }, [filters, refetch]);

  return (
    <Box>
      {/* Filtros y botón */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            size="small"
            label={t("common.search", "Buscar")}
            value={filters.q ?? ""}
            onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value || undefined }))}
          />

          {/* Rol */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Rol</InputLabel>
            <Select
              label="Rol"
              value={filters.rol ?? ""}
              onChange={(e) =>
                setFilters((p) => ({
                  ...p,
                  rol: (e.target.value || undefined) as ProyectoRol | undefined,
                }))
              }
            >
              <MenuItem value="">(Todos)</MenuItem>
              {(meta?.roles ?? []).map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Billing */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Billing</InputLabel>
            <Select
              label="Billing"
              value={filters.billingMode ?? ""}
              onChange={(e) =>
                setFilters((p) => ({
                  ...p,
                  billingMode: (e.target.value || undefined) as ProyectoBillingMode | undefined,
                }))
              }
            >
              <MenuItem value="">(Todos)</MenuItem>
              {(meta?.billingModes ?? []).map((b) => (
                <MenuItem key={b} value={b}>
                  {b}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">Activos</Typography>
            <Switch
              checked={!!filters.activos}
              onChange={(e) => setFilters((p) => ({ ...p, activos: e.target.checked }))}
            />
          </Stack>

          <Box sx={{ flexGrow: 1 }} />
          {canEdit && (
            <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
              {t("common.new", "Nuevo")}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* ═════ LEYENDA DE FLAGS ═════ */}
      <Paper
        sx={{
          p: 1.25,
          mb: 1,
          bgcolor: "grey.50",
          border: "1px dashed #e0e0e0",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Leyenda de flags:
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label="reports" />
            <Typography variant="caption" color="text.secondary">
              Incluye este proyecto en informes generales (métricas/ratios).
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label="docencia" />
            <Typography variant="caption" color="text.secondary">
              Cuenta en horas de profesor (docencia).
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label="alumnos" />
            <Typography variant="caption" color="text.secondary">
              Cuenta en informes de alumno/cliente.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color="warning" label="exam" />
            <Typography variant="caption" color="text.secondary">
              Marca de exámenes (afecta a ciertos reportes).
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label="Training" variant="outlined" />
            <Typography variant="caption" color="text.secondary">
              Texto del <em>grupo de reporte</em> (p. ej. “Training”, “Especial”) cuando está
              definido.
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {isFetching && <LinearProgress sx={{ mb: 1 }} />}

      {/* Tabla */}
      <Paper>
        <Box sx={{ p: 1 }}>
          <Stack direction="row" sx={{ fontWeight: 600, px: 1, py: 1 }}>
            <Box sx={{ width: 160 }}>Código</Box>
            <Box sx={{ width: 280 }}>Nombre</Box>
            <Box sx={{ width: 140 }}>Rol</Box>
            <Box sx={{ width: 160 }}>Billing</Box>
            <Box sx={{ width: 220 }}>Flags</Box>
            <Box sx={{ width: 110 }}>Color</Box>
            <Box sx={{ width: 80 }}>Activo</Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ width: 120, textAlign: "right" }}>Acciones</Box>
          </Stack>

          {(rows ?? []).map((r) => {
            const f_reports = isTruthy(r.includeInReports);
            const f_docencia = isTruthy(r.countsInTeachingHours);
            const f_alumnos = isTruthy(r.countsInStudentReports);
            const f_exam = isTruthy(r.isExamFlag);
            const showGroup = isNonEmptyLabel(r.reportGroup);
            const colorValid = isHexColor(r.calendarColor);

            return (
              <Stack
                key={r.id}
                direction="row"
                sx={{
                  px: 1,
                  py: 1,
                  borderTop: "1px solid #eee",
                  alignItems: "center",
                }}
              >
                <Box sx={{ width: 160 }}>{r.codigo}</Box>
                <Box sx={{ width: 280 }}>{r.nombre}</Box>
                <Box sx={{ width: 140 }}>
                  <Chip size="small" label={r.rol} />
                </Box>
                <Box sx={{ width: 160 }}>
                  <Chip size="small" label={r.billingMode} />
                </Box>

                {/* Flags (solo si valen 1/true) */}
                <Box
                  sx={{
                    width: 220,
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                  }}
                >
                  {f_reports && <Chip size="small" label="reports" />}
                  {f_docencia && <Chip size="small" label="docencia" />}
                  {f_alumnos && <Chip size="small" label="alumnos" />}
                  {f_exam && <Chip size="small" color="warning" label="exam" />}
                  {showGroup && <Chip size="small" label={String(r.reportGroup)} />}
                </Box>

                {/* Color calendario (#RRGGBB válido) */}
                <Box sx={{ width: 110 }}>
                  {colorValid ? (
                    <Chip
                      size="small"
                      label={r.calendarColor!.trim()}
                      sx={{ bgcolor: r.calendarColor!.trim(), color: "#fff" }}
                    />
                  ) : (
                    "—"
                  )}
                </Box>

                <Box sx={{ width: 80 }}>
                  <Chip
                    size="small"
                    color={isTruthy(r.activo) ? "success" : "default"}
                    label={isTruthy(r.activo) ? "Sí" : "No"}
                  />
                </Box>

                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 120, textAlign: "right" }}>
                  {canEdit ? (
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Tooltip title="Editar">
                        <IconButton onClick={() => onEdit(r)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isTruthy(r.activo) ? "Desactivar" : "Activar"}>
                        <IconButton
                          onClick={() =>
                            activarProyecto.mutate({
                              id: r.id,
                              activo: !isTruthy(r.activo),
                            })
                          }
                          size="small"
                        >
                          <CheckCircle
                            fontSize="small"
                            color={isTruthy(r.activo) ? "success" : "disabled"}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar (lógico)">
                        <IconButton
                          onClick={() => removeProyecto.mutate(r.id)}
                          size="small"
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
              </Stack>
            );
          })}

          {(!rows || rows.length === 0) && !isFetching && (
            <Box sx={{ p: 2, color: "text.secondary" }}>Sin resultados</Box>
          )}
        </Box>
      </Paper>

      {dlgOpen && (
        <ProyectoDialog
          open={dlgOpen}
          initial={editRow ?? undefined}
          onClose={() => setDlgOpen(false)}
          onSaved={() => {
            setDlgOpen(false);
            void refetch();
          }}
        />
      )}
    </Box>
  );
}
